import * as vp from 'vopay';
import { Box, Title, Card, DropdownMenu } from 'vopay';
import { Button, TableFiltersContainer, Table } from 'UI';

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import useFetchPartnerAccountSummaryDetails from '@/hooks/useApi/useFetchPartnerAccountSummaryDetails';
import useFetchPartnerAccountSummary from '@/hooks/useApi/useFetchPartnerAccountSummary';
import { useNavMenu } from '@/providers/NavMenu.provider';
import ActivitySummaryForm from './ActivitySummaryForm';
import { usePermissions } from '@/providers/Permissions.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import useFileDownload from '@/hooks/useFiledDownload';
import { useTranslation } from 'react-i18next';

type AccountSummary = {
  TotalRevenueGenerated: string;
  PartnerIncome: string;
  Currency: string;
  Company: string;
  VopayAccountID: string;
};
const ActivitySummaryList = (): React.JSX.Element => {
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();
  const { getModuleTitle } = useNavMenu();
  const [partnerAccountSummary, getPartnerAccountSummary] = useFetchPartnerAccountSummary({});
  const initValues = { Year: getYear(), Month: getMonth() };
  const { permissions } = usePermissions();
  const { downloadJson } = useFileDownload();
  const [accountSummary, setAccountSummary] = useState<AccountSummary[]>();
  const refreshPartnerActivitySummary = (params: vp.PartnerAccountSummaryRequest) => getPartnerAccountSummary(params);
  const [, getPartnerAccountSummaryDetails] = useFetchPartnerAccountSummaryDetails({});
  const [filtersApplied, setFiltersApplied] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'Reports.ActivitySummaryList' });

  useEffect(() => {
    getPartnerAccountSummary(initValues);
  }, []);
  useEffect(() => {
    setAccountSummary(buildPartnerAccountSummary(partnerAccountSummary));
  }, [partnerAccountSummary]);

  const tableProps: vp.TableProps<AccountSummary> = {
    id: 'AccountActivitySummary',
    version: '2',
    columns: [
      {
        id: 'Company',
        header: t('Table.company'),
        align: 'left',
        field: 'Company',
      },
      {
        id: 'Currency',
        header: t('Table.currency'),
        align: 'left',
        field: 'Currency',
      },
      {
        id: 'TotalRevenueGenerated',
        header: t('Table.totalRevenueGenerated'),
        align: 'left',
        template: ({ data }) => `$${Math.round(Number(data.TotalRevenueGenerated) * 100) / 100}`,
      },
      {
        id: 'PartnerIncome',
        header: t('Table.partnerIncome'),
        align: 'left',
        template: ({ data }) =>
          permissions.ShowPartnerIncome ? `$${Math.round(Number(data.PartnerIncome) * 100) / 100}` : '--',
      },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) => (
          <Link
            to={{
              pathname: `account-activity/${data.Company.replace(/ /g, '')}`,
              search: new URLSearchParams({
                Year: partnerAccountSummary.Year.toString(),
                Month: partnerAccountSummary.Month.toString(),
                VopayAccountID: data.VopayAccountID,
              }).toString(),
            }}
          >
            <Button size="small">{t('Table.view')}</Button>
          </Link>
        ),
      },
    ],
    rows: (accountSummary || []).map((summary) => ({
      id: summary.VopayAccountID,
      data: summary,
    })),
  };

  const search = (values: vp.PartnerAccountSummaryRequest) => {
    refreshPartnerActivitySummary({ Year: values.Year, Month: values.Month });
    setFiltersApplied(String(Number(values.Month)) != getMonth() || String(Number(values.Year)) != getYear());
  };

  return (
    <Box flx="col" gapSize="36px">
      <Box mb="md" jc="sb" ai="center">
        <Title title={getModuleTitle()} />
        <DropdownMenu>
          <DropdownMenu.Trigger asChild>
            <Button id="export-transactions" size="large" iconRight={'keyboard_arrow_down'} disabled={isRoleViewOnly}>
              {t('exportButtonLabel')}
            </Button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Content align="end">
            <DropdownMenu.Item
              key="account-activity-summary-json-download"
              onSelect={(e) => {
                e.preventDefault();
                downloadJson(
                  accountSummary,
                  `account-activity-summary-${partnerAccountSummary.Year}-${partnerAccountSummary.Month}`,
                );
              }}
            >
              {t('exportSummaryOption')}
            </DropdownMenu.Item>
            <DropdownMenu.Item
              key="all-account-activity-summary-json-download"
              onSelect={(e) => {
                e.preventDefault();
                getPartnerAccountSummaryDetails({
                  Year: partnerAccountSummary.Year,
                  Month: partnerAccountSummary.Month,
                });
              }}
            >
              {t('exportSummaryDetailsOption')}
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu>
      </Box>
      <Card
        style={{
          padding: '16px',
        }}
      >
        <TableFiltersContainer filtersApplied={filtersApplied}>
          <TableFiltersContainer.Trigger />
          <TableFiltersContainer.Content>
            <ActivitySummaryForm initValues={initValues} search={search} />
          </TableFiltersContainer.Content>
        </TableFiltersContainer>
        <Table {...tableProps} />
      </Card>
    </Box>
  );

  function buildPartnerAccountSummary(accountSummaryParams: vp.PartnerAccountSummaryResponse): AccountSummary[] {
    const dataReduced = Object.entries(accountSummaryParams?.AccountSummary || {}).map((currencyObj) => {
      const [currency, companyList] = currencyObj;
      const currencySummaries: AccountSummary[] = Object.entries(companyList).map((companyObject) => {
        const [company, companySummary] = companyObject;
        return {
          TotalRevenueGenerated: companySummary.TotalRevenueGenerated,
          PartnerIncome: companySummary.PartnerIncome,
          Currency: currency,
          Company: company,
          VopayAccountID: companySummary.VopayAccountID,
        } as AccountSummary;
      });
      return currencySummaries;
    });
    return dataReduced.flat();
  }
  function getYear() {
    const date = new Date();
    const currentYear = date.getFullYear();
    const month = date.getMonth();

    return String(month === 0 ? currentYear - 1 : currentYear);
  }
  function getMonth() {
    const prevMonth = new Date().getMonth() + 1;
    return String(prevMonth === 0 ? 12 : prevMonth);
  }
};

export default ActivitySummaryList;
